// The homepage hero picture (after Attio): the ClearMakes workspace in a window that
// rises and straightens as you scroll, with one card per product floating in around it:
// an Agents answer in a team chat, an Inbox workflow run, and ClearMeet in a call.
import { useRef } from "react";
import { motion, useScroll, useSpring, useTransform } from "framer-motion";
import {
  Bell, Bot, CheckSquare, ChevronDown, EyeOff, Home, MessageSquareText, Search, Sparkles, Star, Video, Workflow,
} from "lucide-react";
import { HeroBoard } from "@/components/site/atl";
import { integrationByKey } from "@/data/site";
import { ProductChip, WindowDots, useStill, useStory, useTyped } from "./motion";

const INBOX = "/brand/inbox-mark-256.png";
const AGENTS = "/brand/agents-mark-256.png";
const MEET = "/brand/meet-mark-256.png";

/* ── The workspace window ─────────────────────────────────────────────────── */
const SideItem = ({ icon: Icon, label, active = false }: { icon: typeof Home; label: string; active?: boolean }) => (
  <span className={`flex items-center gap-2 rounded-md px-2 py-1.5 ${active ? "bg-[hsl(40_6%_91%)] font-medium text-foreground" : "text-[hsl(0_0%_30%)]"}`}>
    <Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={1.8} /> {label}
  </span>
);

const AppWindow = () => (
  <div className="overflow-hidden rounded-2xl border border-[hsl(0_0%_86%)] bg-background text-left shadow-[0_2px_0_hsl(0_0%_100%)_inset,0_50px_100px_-40px_hsl(216_60%_20%/0.45)]">
    <div className="flex h-10 items-center gap-3 border-b border-border bg-[hsl(40_9%_98%)] px-4">
      <WindowDots />
    </div>
    <div className="grid lg:grid-cols-[228px_1fr]">
      {/* sidebar */}
      <div className="hidden border-r border-border bg-[hsl(40_9%_98%)] p-3 text-[13px] lg:block">
        <div className="flex items-center justify-between px-1">
          <span className="flex items-center gap-2 font-semibold text-foreground">
            <span className="flex h-5 w-5 items-center justify-center rounded-md bg-foreground text-[10px] font-bold text-background">A</span> Acme team <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          </span>
        </div>
        <div className="mt-3 flex gap-1.5">
          <span className="flex flex-1 items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1 text-[12px] text-muted-foreground">
            <Sparkles className="h-3 w-3" /> Quick actions <span className="ml-auto text-[10px]">Ctrl K</span>
          </span>
          <span className="flex items-center rounded-md border border-border bg-background px-1.5 text-muted-foreground"><Search className="h-3 w-3" /></span>
        </div>
        <div className="mt-3 space-y-0.5">
          <SideItem icon={Home} label="Home" active />
          <SideItem icon={Bell} label="Notifications" />
          <SideItem icon={CheckSquare} label="Tasks" />
          <SideItem icon={Workflow} label="Workflows" />
          <SideItem icon={Bot} label="Agents" />
          <SideItem icon={Video} label="Meetings" />
          <SideItem icon={MessageSquareText} label="Smart FAQ" />
        </div>
        <p className="mt-4 px-2 text-[11px] font-medium text-muted-foreground">Products</p>
        <div className="mt-1 space-y-0.5">
          {[
            [INBOX, "Inbox"],
            [AGENTS, "Agents"],
            [MEET, "ClearMeet"],
          ].map(([logo, name]) => (
            <span key={name} className="flex items-center gap-2 px-2 py-1.5 text-[hsl(0_0%_30%)]">
              <img src={logo} alt="" className="h-3.5 w-3.5 brightness-0" /> {name}
            </span>
          ))}
        </div>
        <p className="mt-4 px-2 text-[11px] font-medium text-muted-foreground">Apps</p>
        <div className="mt-1 space-y-0.5">
          {["gmail", "slack", "outlook", "hubspot"].map((k) => {
            const a = integrationByKey(k)!;
            return (
              <span key={k} className="flex items-center gap-2 px-2 py-1.5 text-[hsl(0_0%_30%)]">
                <img src={a.logo} alt="" className="h-3.5 w-3.5 object-contain grayscale" /> {a.name}
              </span>
            );
          })}
        </div>
      </div>

      {/* page */}
      <div className="min-w-0">
        <div className="flex h-12 items-center justify-between border-b border-border px-4 text-[13px]">
          <span className="flex items-center gap-2 text-muted-foreground">
            Home <span className="text-border">/</span> <span className="font-medium text-foreground">Team board</span>
            <Star className="h-3.5 w-3.5" />
          </span>
          <span className="hidden items-center gap-3 sm:flex">
            <span className="flex -space-x-1.5">
              {["bg-[hsl(0_0%_15%)]", "bg-[hsl(216_84%_52%)]", "bg-[hsl(0_0%_60%)]"].map((c, i) => (
                <span key={i} className={`h-6 w-6 rounded-full ring-2 ring-background ${c}`} />
              ))}
            </span>
            <span className="text-muted-foreground">Share</span>
            <span className="flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1 font-medium text-foreground">
              <Sparkles className="h-3.5 w-3.5" /> Ask ClearMakes
            </span>
          </span>
        </div>
        <div className="p-3 sm:p-4">
          <HeroBoard bare />
        </div>
      </div>
    </div>
  </div>
);

/* ── Floating cards, one per product ──────────────────────────────────────── */
const card = "rounded-2xl border border-[hsl(0_0%_88%)] bg-background p-3.5 text-left shadow-[0_30px_60px_-30px_hsl(216_60%_20%/0.45)]";

const AgentsCard = () => {
  const { ref, step } = useStory(3, 1100, 3200);
  const answer = useTyped("30 days from delivery. You can start it from the order page.", step >= 2, 22);
  return (
    <div ref={ref} className={card}>
      <div className="flex items-center justify-between">
        <span className="text-[13px] font-semibold text-foreground">Support agent</span>
        <ProductChip logo={AGENTS} name="ClearMakes Agents" />
      </div>
      <div className="mt-3 flex gap-2.5">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-[hsl(0_0%_88%)] text-[11px] font-bold text-foreground">MR</span>
        <div className="min-w-0">
          <p className="text-[12px] font-semibold text-foreground">Maya <span className="font-normal text-muted-foreground">10:24</span></p>
          <p className="text-[13px] leading-snug text-foreground">What is our refund window?</p>
        </div>
      </div>
      <div className={`mt-3 flex gap-2.5 transition-opacity duration-300 ${step >= 1 ? "opacity-100" : "opacity-0"}`}>
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-foreground"><img src={AGENTS} alt="" className="h-4 w-4 brightness-0 invert" /></span>
        <div className="min-w-0">
          <p className="text-[12px] font-semibold text-foreground">
            Support agent <span className="ml-1 rounded bg-[hsl(40_9%_93%)] px-1 text-[9.5px] font-bold text-muted-foreground">APP</span>{" "}
            <span className="font-normal text-muted-foreground">10:24</span>
          </p>
          <p className="min-h-[36px] text-[13px] leading-snug text-foreground">{step >= 2 ? answer : <span className="text-muted-foreground">Looking in your documents…</span>}</p>
        </div>
      </div>
    </div>
  );
};

const RUN = [
  "New email from Dana at Acme",
  "Summarised: invoice #2041 due Friday",
  "Posted to #finance in Slack",
  "Done. Nothing waiting for you.",
];

const InboxRunCard = () => {
  const { ref, step } = useStory(RUN.length + 1, 750, 2800);
  return (
    <div ref={ref} className="rounded-2xl border border-[hsl(0_0%_100%/0.08)] bg-[hsl(0_0%_7%)] p-4 text-left font-mono shadow-[0_30px_60px_-30px_hsl(0_0%_0%/0.6)]">
      <div className="flex items-center justify-between">
        <WindowDots dark />
        <ProductChip logo={INBOX} name="ClearMakes Inbox" dark />
      </div>
      <p className="mt-3 rounded-md bg-[hsl(0_0%_100%/0.06)] px-2.5 py-2 text-[12px] leading-snug text-[hsl(0_0%_95%)]">
        &gt; When a customer emails, summarise it and post it to Slack.
      </p>
      <ul className="mt-3 space-y-1.5 text-[12px] leading-snug">
        {RUN.map((line, i) => (
          <li
            key={line}
            className={`flex items-start gap-2 transition-all duration-300 ${step > i ? "translate-y-0 opacity-100" : "translate-y-1 opacity-0"} ${
              i === RUN.length - 1 ? "text-[hsl(216_90%_72%)]" : "text-[hsl(0_0%_78%)]"
            }`}
          >
            <span className={`mt-[5px] h-1.5 w-1.5 shrink-0 rounded-full ${i === RUN.length - 1 ? "bg-[hsl(216_90%_65%)]" : "bg-[hsl(0_0%_55%)]"}`} />
            {line}
          </li>
        ))}
      </ul>
    </div>
  );
};

const MeetCard = () => {
  const { ref, step } = useStory(3, 1200, 3400);
  const answer = useTyped("At $18 a seat, forty people come to $720 a month.", step >= 2, 24);
  return (
    <div ref={ref} className={card}>
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-[13px] font-semibold text-foreground">Weekly pilot review</span>
        <ProductChip logo={MEET} name="ClearMeet" />
      </div>
      <div className="relative mt-3 flex h-[92px] items-center justify-center overflow-hidden rounded-xl bg-[linear-gradient(135deg,hsl(216_30%_22%),hsl(216_40%_12%))]">
        <span className={`flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(216_20%_75%)] text-[15px] font-bold text-[hsl(216_40%_15%)] ${step === 0 ? "ring-4 ring-[hsl(216_84%_60%)]" : ""}`}>SK</span>
        <span className="absolute bottom-1.5 left-2 text-[11px] font-medium text-[hsl(0_0%_95%)]">Sam</span>
        <span className="absolute bottom-2 right-2 flex h-3 items-end gap-[2px]">
          {[0, 1, 2, 3].map((i) => (
            <span key={i} className={`w-[3px] rounded-full bg-[hsl(0_0%_95%)] ${step === 0 ? "cm-bar" : "h-1"}`} style={{ animationDelay: `${i * 0.12}s` }} />
          ))}
        </span>
      </div>
      <p className="mt-3 text-[12px] text-muted-foreground">Sam asked</p>
      <p className="text-[13px] font-medium leading-snug text-foreground">What would a pilot cost for forty people?</p>
      <div className="mt-2.5 min-h-[58px] rounded-lg border-l-2 border-[hsl(216_84%_52%)] bg-[hsl(216_90%_97%)] px-2.5 py-2">
        <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[hsl(216_84%_42%)]">Say this</p>
        <p className="text-[13px] leading-snug text-foreground">{step >= 2 ? answer : step === 1 ? <span className="text-muted-foreground">Thinking…</span> : " "}</p>
      </div>
      <p className="mt-2.5 flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
        <EyeOff className="h-3.5 w-3.5" /> Hidden when you share your screen
      </p>
    </div>
  );
};

/* ── The stage ────────────────────────────────────────────────────────────── */
export const HeroStage = () => {
  const ref = useRef<HTMLDivElement>(null);
  const still = useStill();
  // 0 when the window's top is at the bottom of the screen, 1 when it is 20% from the top.
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "start 20%"] });
  const p = useSpring(scrollYProgress, { stiffness: 140, damping: 30, mass: 0.35 });
  const y = useTransform(p, [0, 1], [120, 0]);
  const scale = useTransform(p, [0, 1], [0.86, 1]);
  const rotateX = useTransform(p, [0, 1], [20, 0]);
  // the side cards stay out of sight on load and arrive once you start scrolling
  const side = useTransform(p, [0.74, 0.92], [0, 1]);
  const inL = useTransform(p, [0.74, 1], [110, 0]);
  const inR = useTransform(p, [0.74, 1], [-110, 0]);
  const lift = useTransform(p, [0.74, 1], [70, 0]);
  const liftSlow = useTransform(p, [0.74, 1], [130, 0]);

  const float = (x: typeof inL, yy: typeof lift) => (still ? undefined : { opacity: side, x, y: yy });

  return (
    <div ref={ref} className="relative mx-auto w-full max-w-[1520px]" style={{ perspective: 1800 }}>
      <motion.div
        style={still ? undefined : { y, scale, rotateX, transformOrigin: "50% 0%" }}
        className="relative z-10 mx-auto w-full max-w-[1120px]"
      >
        <AppWindow />
      </motion.div>

      <motion.div style={float(inL, lift)} className="absolute left-0 top-[6%] z-20 hidden w-[290px] lg:block xl:w-[310px]">
        <AgentsCard />
      </motion.div>
      <motion.div style={float(inL, liftSlow)} className="absolute bottom-[6%] left-[1.5%] z-20 hidden w-[300px] lg:block xl:w-[330px]">
        <InboxRunCard />
      </motion.div>
      <motion.div style={float(inR, lift)} className="absolute right-0 top-[14%] z-20 hidden w-[280px] lg:block xl:w-[300px]">
        <MeetCard />
      </motion.div>
    </div>
  );
};
